import React, { useState } from "react";

/**
 * FocalPointPicker — lets the editor pick where to focus the crop.
 *
 * Props:
 *   url      image url
 *   value    "50% 40%" style (defaults to center)
 *   onChange (str) => void
 *
 * Features:
 *  - 9 preset spots (top-left, top-center, top-right, mid-left, center, mid-right,
 *    bottom-left, bottom-center, bottom-right)
 *  - Custom: click anywhere on the image to set the focal point
 *  - Live preview: shows a marker where the focal point is
 *  - The value drives `object-position` in all cards on the site
 */
export default function FocalPointPicker({ url, value = "50% 50%", onChange }) {
  const [dragging, setDragging] = useState(false);

  const parse = (v) => {
    const m = String(v || "").match(/(-?\d+(?:\.\d+)?)%\s+(-?\d+(?:\.\d+)?)%/);
    if (!m) return { x: 50, y: 50 };
    return { x: parseFloat(m[1]), y: parseFloat(m[2]) };
  };
  const { x, y } = parse(value);

  const setFromEvent = (e) => {
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    let nx = ((clientX - rect.left) / rect.width) * 100;
    let ny = ((clientY - rect.top) / rect.height) * 100;
    nx = Math.max(0, Math.min(100, Math.round(nx)));
    ny = Math.max(0, Math.min(100, Math.round(ny)));
    onChange(`${nx}% ${ny}%`);
  };

  const presets = [
    { label: "↖", value: "0% 0%", title: "Arriba izquierda" },
    { label: "↑", value: "50% 0%", title: "Arriba centro" },
    { label: "↗", value: "100% 0%", title: "Arriba derecha" },
    { label: "←", value: "0% 50%", title: "Centro izquierda" },
    { label: "●", value: "50% 50%", title: "Centro" },
    { label: "→", value: "100% 50%", title: "Centro derecha" },
    { label: "↙", value: "0% 100%", title: "Abajo izquierda" },
    { label: "↓", value: "50% 100%", title: "Abajo centro" },
    { label: "↘", value: "100% 100%", title: "Abajo derecha" },
  ];

  return (
    <div className="mt-4 pt-4 border-t border-softblue" data-testid="focal-point-picker">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs uppercase tracking-widest text-textgray font-semibold">
          Punto focal de la imagen
        </label>
        <span className="text-[11px] text-textgray font-mono">{value || "50% 50%"}</span>
      </div>
      <p className="text-[11px] text-textgray mb-3">
        Definí dónde debe centrarse la foto al recortarse en tarjetas y hero. Podés hacer clic
        directo sobre la imagen o usar los 9 accesos rápidos.
      </p>

      {/* Interactive picker */}
      <div
        className="relative w-full overflow-hidden bg-blueblack border border-softblue select-none cursor-crosshair"
        style={{ aspectRatio: "16 / 9" }}
        onMouseDown={(e) => { setDragging(true); setFromEvent(e); }}
        onMouseMove={(e) => { if (dragging) setFromEvent(e); }}
        onMouseUp={() => setDragging(false)}
        onMouseLeave={() => setDragging(false)}
        onTouchStart={(e) => { setDragging(true); setFromEvent(e); }}
        onTouchMove={(e) => { if (dragging) setFromEvent(e); }}
        onTouchEnd={() => setDragging(false)}
        data-testid="focal-point-canvas"
      >
        <img
          src={url}
          alt="focal-preview"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: `${x}% ${y}%` }}
          draggable={false}
        />
        {/* Focal point marker */}
        <div
          className="absolute w-8 h-8 -ml-4 -mt-4 rounded-full border-2 border-white shadow-lg pointer-events-none flex items-center justify-center"
          style={{ left: `${x}%`, top: `${y}%`, backgroundColor: "rgba(0,166,166,0.5)" }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-white" />
        </div>
      </div>

      {/* 3x3 preset grid */}
      <div className="mt-3 grid grid-cols-3 gap-1 w-32">
        {presets.map((p) => {
          const active = value === p.value;
          return (
            <button
              key={p.value}
              type="button"
              onClick={() => onChange(p.value)}
              title={p.title}
              data-testid={`focal-preset-${p.value.replace(/[%\s]/g, "")}`}
              className={`h-8 flex items-center justify-center text-sm font-bold border transition-colors ${
                active
                  ? "bg-turquoise text-white border-turquoise"
                  : "bg-white text-textgray border-softblue hover:border-nightblue hover:text-nightblue"
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
